import React, { useState } from 'react';
import { CheckCircle, Clock, FileText, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { IntelligenceProfile } from '../../types';

interface AnalysisQueuedSectionProps {
  profile: IntelligenceProfile;
  className?: string;
}

export const AnalysisQueuedSection: React.FC<AnalysisQueuedSectionProps> = ({
  profile,
  className = ""
}) => {
  const [showProcessLog, setShowProcessLog] = useState(false);
  const [executionLog, setExecutionLog] = useState<any>(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  // Get status from submission data
  const processingStatus = profile.submission?.processing_status || 'pending';
  const trackingId = profile.tracking_id;

  // Don't render if not submitted
  if (!trackingId) {
    return null;
  }

  const getStatusDisplay = () => {
    switch (processingStatus) {
      case 'pending':
        return {
          icon: <Clock className="h-5 w-5 text-blue-600" />,
          title: 'Analysis Queued Successfully!',
          message: 'Your analysis is queued for processing and will begin shortly.',
          bgColor: 'bg-blue-50',
          borderColor: 'border-l-blue-400',
          textColor: 'text-blue-900'
        };
      case 'processing':
        return {
          icon: <Clock className="h-5 w-5 text-orange-600 animate-pulse" />,
          title: 'Analysis In Progress',
          message: 'Your analysis is currently being processed in the background.',
          bgColor: 'bg-orange-50',
          borderColor: 'border-l-orange-400',
          textColor: 'text-orange-900'
        };
      case 'completed':
        return {
          icon: <CheckCircle className="h-5 w-5 text-green-600" />,
          title: 'Analysis Completed Successfully!',
          message: 'Your analysis has been completed. You can review the execution log below.',
          bgColor: 'bg-green-50',
          borderColor: 'border-l-green-400',
          textColor: 'text-green-900'
        };
      case 'failed':
        return {
          icon: <Clock className="h-5 w-5 text-red-600" />,
          title: 'Analysis Will Retry',
          message: 'There was an issue processing your analysis. It will be retried automatically.',
          bgColor: 'bg-red-50',
          borderColor: 'border-l-red-400',
          textColor: 'text-red-900'
        };
      default:
        return {
          icon: <Clock className="h-5 w-5 text-gray-600" />,
          title: 'Analysis Status Unknown',
          message: 'Please check the status using the link below.',
          bgColor: 'bg-gray-50',
          borderColor: 'border-l-gray-400',
          textColor: 'text-gray-900'
        };
    }
  };

  const statusDisplay = getStatusDisplay();


  const fetchExecutionLog = async () => {
    if (!trackingId) return;

    setLoadingLog(true);
    setLogError(null);

    try {
      const response = await fetch(`/api/execution-logs/${trackingId}`);
      const data = await response.json();

      if (data.success) {
        setExecutionLog(data.data);
      } else {
        setLogError(data.error || 'Failed to load execution log');
      }
    } catch (error) {
      console.error('Failed to fetch execution log:', error);
      setLogError('Failed to connect to execution log service');
    } finally {
      setLoadingLog(false);
    }
  };

  const handleProcessLogClick = () => {
    setShowProcessLog(true);
    fetchExecutionLog();
  };

  return (
    <div className={`p-4 rounded-lg border-l-4 ${statusDisplay.borderColor} ${statusDisplay.bgColor} ${className}`}>
      <div className="flex items-start gap-3">
        {statusDisplay.icon}
        <div className="flex-1">
          <h4 className={`text-sm font-semibold ${statusDisplay.textColor} mb-2`}>
            {statusDisplay.title}
          </h4>
          <p className={`text-sm ${statusDisplay.textColor} opacity-80 mb-3`}>
            {statusDisplay.message}
          </p>
          <p className={`text-xs ${statusDisplay.textColor} opacity-70 mb-3`}>
            <strong>Typical completion time:</strong> 15-30 minutes<br/>
            <strong>Tracking ID:</strong> {trackingId}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleProcessLogClick}
              className="flex items-center gap-2 text-xs"
            >
              <FileText className="h-3 w-3" />
              Process Log
            </Button>
          </div>
        </div>
      </div>

      {/* Process Log Modal */}
      {showProcessLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowProcessLog(false)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Process Log - {trackingId}</h3>
              <Button variant="outline" size="sm" onClick={() => setShowProcessLog(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="bg-gray-50 p-4 rounded border text-sm">
              {loadingLog ? (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  Loading execution log...
                </div>
              ) : logError ? (
                <div className="text-red-600">
                  <div className="font-semibold mb-2">❌ Error Loading Log</div>
                  <div>{logError}</div>
                  <div className="mt-3 text-gray-600 text-xs">
                    This may indicate the analysis hasn't started yet or logs are not available.
                  </div>
                </div>
              ) : executionLog ? (
                <div className="font-mono">
                  <div className="text-blue-600 font-semibold mb-3">📋 Execution Log for {trackingId}</div>

                  <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
                    <div><strong>Company:</strong> {executionLog.company}</div>
                    <div><strong>Tier:</strong> {executionLog.tier}</div>
                    <div><strong>Started:</strong> {new Date(executionLog.started_at).toLocaleString()}</div>
                    <div><strong>Duration:</strong> {executionLog.duration_readable}</div>
                    <div><strong>Total Steps:</strong> {executionLog.total_steps}</div>
                    <div><strong>Final Status:</strong>
                      <span className={`ml-1 px-2 py-1 rounded text-xs ${
                        executionLog.final_status === 'completed' ? 'bg-green-100 text-green-800' :
                        executionLog.final_status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {executionLog.final_status}
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-3">
                    <div className="font-semibold mb-2">Execution Steps:</div>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {executionLog.steps.map((step: any, index: number) => (
                        <div key={index} className={`p-2 rounded border-l-4 ${
                          step.status === 'completed' ? 'border-green-400 bg-green-50' :
                          step.status === 'failed' ? 'border-red-400 bg-red-50' :
                          'border-blue-400 bg-blue-50'
                        }`}>
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-semibold">{step.step}</span>
                            <span className="text-xs text-gray-500">+{step.time_from_start}</span>
                          </div>
                          <div className="text-xs">{step.message}</div>
                          {step.error_details && (
                            <div className="text-xs text-red-600 mt-1 p-1 bg-red-100 rounded">
                              {step.error_details}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-600">
                  No execution log data available for {trackingId}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};